import { signal } from '@preact/signals';

const DATE_RANGE_PRESETS = ['live', 'today', 'last_7_days', 'last_30_days', 'current_month', 'all_time', 'custom'] as const;
const ALL_TIME_START_ISO = '1970-01-01T00:00:00.000Z';
const DASHBOARD_FILTER_FIELDS = [
    'page',
    'source',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'channel',
    'browser',
    'operating_system',
    'screen_size',
    'country',
    'region',
    'city',
] as const;

export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number];
export type DashboardFilterField = (typeof DASHBOARD_FILTER_FIELDS)[number];
export type DashboardFilters = Partial<Record<DashboardFilterField, string[]>>;
export type DashboardEmbedMode = '1' | '2' | null;

const toDateOnlyIso = (date: Date) => date.toISOString().split('T')[0] ?? '';

const isValidDate = (value: string | null | undefined) => {
    if (!value) {
        return false;
    }

    return !Number.isNaN(new Date(value).getTime());
};

const isDateRangePreset = (value: string | null | undefined): value is DateRangePreset =>
    Boolean(value && DATE_RANGE_PRESETS.includes(value as DateRangePreset));

const normalizeFilterValues = (values: string[]) =>
    [...new Set(
        values
            .flatMap(value => value.split(','))
            .map(value => value.trim())
            .filter(Boolean)
    )];

const normalizeDashboardFilters = (filters: DashboardFilters) => {
    const normalizedFilters: DashboardFilters = {};

    DASHBOARD_FILTER_FIELDS.forEach((field) => {
        const values = normalizeFilterValues(filters[field] ?? []);
        if (values.length > 0) {
            normalizedFilters[field] = values;
        }
    });

    return normalizedFilters;
};

const readDashboardFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const filters: DashboardFilters = {};

    DASHBOARD_FILTER_FIELDS.forEach((field) => {
        const values = normalizeFilterValues(params.getAll(`filter_${field}`));
        if (values.length > 0) {
            filters[field] = values;
        }
    });

    return filters;
};

const getDateRangeForPreset = (preset: DateRangePreset, now = new Date()) => {
    switch (preset) {
        case 'live':
            return {
                start: toDateOnlyIso(now),
                end: now.toISOString(),
            };
        case 'today': {
            const priorDay = new Date(now);
            priorDay.setDate(now.getDate() - 1);
            return {
                start: toDateOnlyIso(priorDay),
                end: now.toISOString(),
            };
        }
        case 'last_7_days': {
            const priorWeek = new Date(now);
            priorWeek.setDate(now.getDate() - 7);
            return {
                start: toDateOnlyIso(priorWeek),
                end: now.toISOString(),
            };
        }
        case 'last_30_days': {
            const priorMonth = new Date(now);
            priorMonth.setDate(now.getDate() - 30);
            return {
                start: toDateOnlyIso(priorMonth),
                end: now.toISOString(),
            };
        }
        case 'current_month': {
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
            return {
                start: firstDay.toISOString(),
                end: now.toISOString(),
            };
        }
        case 'all_time':
            return {
                start: ALL_TIME_START_ISO,
                end: now.toISOString(),
            };
        case 'custom':
            return {
                start: toDateOnlyIso(now),
                end: toDateOnlyIso(now),
            };
    }
};

function readPropertyFromUrl() {
    return new URLSearchParams(window.location.search).get('property') ?? '';
}

export const selectedProperty = signal(readPropertyFromUrl());

function readInitialDateRange() {
    const params = new URLSearchParams(window.location.search);
    const presetFromUrl = params.get('dateRangePreset') ?? params.get('date_range_preset');
    const startFromUrl = params.get('start');
    const endFromUrl = params.get('end');

    if (isDateRangePreset(presetFromUrl)) {
        const fallbackRange = getDateRangeForPreset(presetFromUrl);
        return {
            preset: presetFromUrl,
            start: isValidDate(startFromUrl) ? (startFromUrl as string) : fallbackRange.start,
            end: isValidDate(endFromUrl) ? (endFromUrl as string) : fallbackRange.end,
        };
    }

    if (isValidDate(startFromUrl) || isValidDate(endFromUrl)) {
        const fallbackRange = getDateRangeForPreset('custom');
        return {
            preset: 'custom' as DateRangePreset,
            start: isValidDate(startFromUrl) ? (startFromUrl as string) : fallbackRange.start,
            end: isValidDate(endFromUrl) ? (endFromUrl as string) : fallbackRange.end,
        };
    }

    const fallbackRange = getDateRangeForPreset('live');
    return {
        preset: 'live' as DateRangePreset,
        start: fallbackRange.start,
        end: fallbackRange.end,
    };
}

const initialDateRange = readInitialDateRange();

export const startDate = signal(initialDateRange.start);
export const endDate = signal(initialDateRange.end);
export const dateRangePreset = signal<DateRangePreset>(initialDateRange.preset);
export const dashboardFilters = signal<DashboardFilters>(readDashboardFiltersFromUrl());

// Stato di autenticazione
export const isAuthenticated = signal<boolean | null>(null);
const embeddedDashboardMode = signal<DashboardEmbedMode>(readEmbedModeFromUrl());

function readAuthTokenFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('authToken');
}

function readBooleanParam(name: string) {
    const value = new URLSearchParams(window.location.search).get(name)?.toLowerCase();
    return value === '1' || value === 'true' || value === 'yes';
}

function readEmbedModeFromUrl(): DashboardEmbedMode {
    const value = new URLSearchParams(window.location.search).get('embed')?.trim().toLowerCase();

    if (!value || value === '0' || value === 'false' || value === 'no') {
        return null;
    }

    if (value === '2') {
        return '2';
    }

    return '1';
}

export const authToken = signal<string | null>(readAuthTokenFromUrl());

export function setAuthenticationState(value: boolean) {
    isAuthenticated.value = value;
}

function applyDashboardStateToUrl(url: URL) {
    if (selectedProperty.value) {
        url.searchParams.set('property', selectedProperty.value);
    } else {
        url.searchParams.delete('property');
    }

    url.searchParams.set('dateRangePreset', dateRangePreset.value);
    url.searchParams.delete('date_range_preset');

    if (startDate.value) {
        url.searchParams.set('start', startDate.value);
    } else {
        url.searchParams.delete('start');
    }

    if (endDate.value) {
        url.searchParams.set('end', endDate.value);
    } else {
        url.searchParams.delete('end');
    }

    DASHBOARD_FILTER_FIELDS.forEach((field) => {
        url.searchParams.delete(`filter_${field}`);
        (dashboardFilters.value[field] ?? []).forEach((value) => {
            url.searchParams.append(`filter_${field}`, value);
        });
    });
}

function applyDashboardContextToUrl(url: URL) {
    if (authToken.value) {
        url.searchParams.set('authToken', authToken.value);
    } else {
        url.searchParams.delete('authToken');
    }

    if (embeddedDashboardMode.value) {
        url.searchParams.set('embed', embeddedDashboardMode.value);
    } else {
        url.searchParams.delete('embed');
    }
}

export function syncDashboardStateToUrl() {
    const url = new URL(window.location.href);
    applyDashboardStateToUrl(url);
    applyDashboardContextToUrl(url);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export function setDateRangeState(preset: DateRangePreset, start: string, end: string, options?: { updateUrl?: boolean }) {
    dateRangePreset.value = preset;
    startDate.value = start;
    endDate.value = end;

    if (options?.updateUrl === false) {
        return;
    }

    syncDashboardStateToUrl();
}

export function applyDateRangePreset(preset: DateRangePreset, options?: { updateUrl?: boolean }) {
    const range = getDateRangeForPreset(preset);
    setDateRangeState(preset, range.start, range.end, options);
}

export function setDashboardFilters(value: DashboardFilters, options?: { updateUrl?: boolean }) {
    dashboardFilters.value = normalizeDashboardFilters(value);

    if (options?.updateUrl === false) {
        return;
    }

    syncDashboardStateToUrl();
}

export function toggleDashboardFilter(field: DashboardFilterField, value: string, options?: { updateUrl?: boolean }) {
    const currentValues = dashboardFilters.value[field] ?? [];
    const nextValues = currentValues.includes(value)
        ? currentValues.filter((entry) => entry !== value)
        : [...currentValues, value];

    const nextFilters = { ...dashboardFilters.value };
    if (nextValues.length > 0) {
        nextFilters[field] = nextValues;
    } else {
        delete nextFilters[field];
    }

    setDashboardFilters(nextFilters, options);
}

export function clearDashboardFilters(options?: { updateUrl?: boolean }) {
    setDashboardFilters({}, options);
}

export function removeDashboardFilter(field: DashboardFilterField, value: string, options?: { updateUrl?: boolean }) {
    const currentValues = dashboardFilters.value[field] ?? [];
    if (currentValues.length === 0) {
        return;
    }

    const nextValues = currentValues.filter((entry) => entry !== value);
    const nextFilters = { ...dashboardFilters.value };
    if (nextValues.length > 0) {
        nextFilters[field] = nextValues;
    } else {
        delete nextFilters[field];
    }

    setDashboardFilters(nextFilters, options);
}

export function appendDashboardFiltersToSearchParams(params: URLSearchParams) {
    DASHBOARD_FILTER_FIELDS.forEach((field) => {
        (dashboardFilters.value[field] ?? []).forEach((value) => {
            params.append(`filter_${field}`, value);
        });
    });
}

export function isDashboardFilterActive(field: DashboardFilterField, value: string) {
    return (dashboardFilters.value[field] ?? []).includes(value);
}

export function getDashboardFilterLabel(field: DashboardFilterField) {
    const labels: Record<DashboardFilterField, string> = {
        page: 'Pagina',
        source: 'Fonte',
        utm_source: 'UTM Source',
        utm_medium: 'UTM Medium',
        utm_campaign: 'UTM Campaign',
        channel: 'Canale',
        browser: 'Browser',
        operating_system: 'Sistema operativo',
        screen_size: 'Risoluzione',
        country: 'Paese',
        region: 'Regione',
        city: 'Citta',
    };

    return labels[field];
}

export function setSelectedProperty(value: string, options?: { updateUrl?: boolean }) {
    selectedProperty.value = value;

    if (options?.updateUrl === false) {
        return;
    }

    syncDashboardStateToUrl();
}

export function buildLoginUrl() {
    const url = new URL('/dashboard/login', window.location.origin);
    if (authToken.value) {
        url.searchParams.set('authToken', authToken.value);
    }
    if (embeddedDashboardMode.value) {
        url.searchParams.set('embed', embeddedDashboardMode.value);
    }
    return `${url.pathname}${url.search}`;
}

export function isEmbeddedDashboardMode() {
    return embeddedDashboardMode.value !== null;
}

export function getDashboardEmbedMode() {
    return embeddedDashboardMode.value;
}

export function buildDashboardUrl(path: string, options?: { preserveCurrentSearch?: boolean }) {
    const url = new URL(path, window.location.origin);

    if (options?.preserveCurrentSearch) {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.forEach((value, key) => {
            url.searchParams.append(key, value);
        });
        applyDashboardContextToUrl(url);
        return `${url.pathname}${url.search}${url.hash}`;
    }

    applyDashboardStateToUrl(url);
    applyDashboardContextToUrl(url);

    return `${url.pathname}${url.search}${url.hash}`;
}

export function clearAuthTokenFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('authToken');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}
